import {
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useRef,
} from "react";

interface ModalProps {
  open: boolean;
  title?: string;
  children: ReactNode;
  onClose: () => void;
  closeOnOverlayClick?: boolean;
  className?: string;
}

const focusableSelector =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  title,
  children,
  onClose,
  closeOnOverlayClick = true,
  className,
}: ModalProps): JSX.Element | null {
  const panelRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = panelRef.current?.querySelector<HTMLElement>(focusableSelector);
    (first ?? panelRef.current)?.focus();
    return () => previous?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm md:items-center md:p-4">
      <button
        type="button"
        aria-label="モーダルを閉じる"
        tabIndex={-1}
        className="absolute inset-0 cursor-default"
        onClick={closeOnOverlayClick ? onClose : undefined}
      />
      <dialog
        open
        ref={panelRef}
        tabIndex={-1}
        aria-label={title === undefined ? "ダイアログ" : undefined}
        aria-labelledby={title === undefined ? undefined : titleId}
        aria-modal="true"
        onKeyDown={handleKeyDown(onClose, panelRef)}
        className={cx(
          "relative m-0 max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-xl border border-border bg-surface-elevated p-5 text-text-primary shadow-floating md:rounded-xl",
          className,
        )}
      >
        {title !== undefined && (
          <h2 id={titleId} className="mb-4 text-lg font-semibold text-text-primary">
            {title}
          </h2>
        )}
        {children}
      </dialog>
    </div>
  );
}

function handleKeyDown(onClose: () => void, panelRef: RefObject<HTMLDialogElement>) {
  return (event: KeyboardEvent<HTMLDialogElement>): void => {
    if (event.key === "Escape") onClose();
    if (event.key === "Tab") trapFocus(event, panelRef.current);
  };
}

function trapFocus(event: KeyboardEvent<HTMLDialogElement>, panel: HTMLDialogElement | null): void {
  const focusable = Array.from(panel?.querySelectorAll<HTMLElement>(focusableSelector) ?? []);
  if (focusable.length === 0) {
    event.preventDefault();
    panel?.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable.at(-1);
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last?.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first?.focus();
  }
}

function cx(...classes: Array<string | false | undefined>): string {
  return classes.filter(Boolean).join(" ");
}
